import axios from "axios";
import client from "./client";
import { deviceIdHeaders } from "../lib/deviceId";
import { parseQueue, type CastQueue } from "../lib/castQueue";

/** room-stc cast-slot writes (Android CastService); base /api/content/. */
export interface CastTimelineDto { playStatus?: string; seekTime?: number; playStartTime?: number; rev?: number; serverTime?: number }
export interface CastEnvelope {
  result?: boolean;
  timeline?: CastTimelineDto | null;
  /** The slot's Queue (spec 2026-10-16 §3.5); parse with parseQueue. */
  queue?: unknown;
  /** `next` ran past the last item: the cast was stopped (a STOP_STREAM follows). */
  stopped?: boolean;
}

/**
 * A refused cast call. `serverMessage` is the envelope's message (a content code or main-api's
 * slot-rule text); `code` is a string `errorCode` when the server sends one; `body` is the whole
 * error body (STALE_INDEX carries the current `queue` beside the envelope).
 */
export class CastError extends Error {
  status: number;
  serverMessage: string | null;
  code: string | null;
  body: unknown;
  constructor(status: number, serverMessage: string | null, code: string | null = null, body: unknown = null) {
    super(serverMessage ?? `HTTP ${status}`);
    this.name = "CastError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.code = code;
    this.body = body;
  }
}

async function call<T>(req: Promise<{ data: T }>): Promise<T> {
  try {
    return (await req).data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const data = e.response?.data as { message?: unknown; errorCode?: unknown } | undefined;
      const msg = data?.message;
      throw new CastError(
        e.response?.status ?? 0,
        typeof msg === "string" && msg.trim() ? msg.trim() : null,
        typeof data?.errorCode === "string" ? data.errorCode : null,
        e.response?.data ?? null,
      );
    }
    throw e;
  }
}

const base = (slotId: number) => `/api/content/cast/slots/${slotId}`;

export const castMedia = (slotId: number, mediaId: number) => call<CastEnvelope>(client.post(`${base(slotId)}/media`, { mediaId }));
export const castLink = (slotId: number, url: string) => call<CastEnvelope>(client.post(`${base(slotId)}/link`, { url }));
export const pauseCast = (slotId: number, seekTime: number) => call<CastEnvelope>(client.post(`${base(slotId)}/pause`, { seekTime }));
/** No position: resume where it paused (`{}`); a position on a playing cast re-anchors it. */
export const resumeCast = (slotId: number, seekTime?: number) =>
  call<CastEnvelope>(client.post(`${base(slotId)}/resume`, seekTime == null ? {} : { seekTime }));
export const pingCast = async (slotId: number) => (await call<{ result?: boolean }>(client.post(`${base(slotId)}/ping`))).result === true;
export const stopCast = async (slotId: number) => (await call<{ result?: boolean }>(client.post(`${base(slotId)}/stop`))).result === true;

/** For pagehide: survives the page going away. Auth rides on the session cookie (same-origin relative URL, like stopStreamingKeepalive). */
export function stopCastKeepalive(slotId: number): void {
  fetch(`${base(slotId)}/stop`, { method: "POST", keepalive: true, credentials: "include", headers: deviceIdHeaders() }).catch(() => {});
}

/** room-stc's 400 messages for a slot that is no longer this user's playing cast (Android OwnVmplCast.CAST_GONE). */
export const CAST_GONE = /Slot \d+ is not found ?!|Slot \d+ is not playing ?!|User \S+ is not who 's streaming the slot \d+ ?!?/;
export const isCastGone = (e: unknown): boolean =>
  e instanceof CastError && e.status === 400 && e.serverMessage != null && CAST_GONE.test(e.serverMessage);

/** Stream a post on the GC's wall into its link slot (spec 2026-10-16 §3.2). */
export const castPost = (slotId: number, postId: string) => call<CastEnvelope>(client.post(`${base(slotId)}/post`, { postId }));

/** `to` omitted: the next item; `to = fromIndex − 1`: Previous; `to = fromIndex`: restart. No client sends asHost yet. */
export interface NextBody { fromIndex: number; to?: number; asHost?: boolean }
export const nextCast = (slotId: number, body: NextBody) => call<CastEnvelope>(client.post(`${base(slotId)}/next`, body));

/** Spec 2026-10-19 §3.2: Accept the prompt `promptId` (prompt_at in Unix ms). 200 is `{result: true, nextPromptAt}`. */
export const acceptKeepalive = (slotId: number, promptId: number) =>
  call<{ result?: boolean; nextPromptAt?: number }>(client.post(`${base(slotId)}/keepalive`, { promptId }));
/** Someone else accepted first, or the prompt moved on: the client closes the dialog. */
export const isStalePrompt = (e: unknown): e is CastError => e instanceof CastError && e.status === 409 && hasCastCode(e, "STALE_PROMPT");

export interface CastQueueState { queue: CastQueue | null; keepalive: unknown }
/** GET queue with its keep-alive state (spec §3.2 "late joiners"): `keepalive` sits beside the queue's fields in `result`. */
export async function getCastQueueState(slotId: number): Promise<CastQueueState> {
  const env = await call<{ result?: unknown }>(client.get(`${base(slotId)}/queue`));
  return { queue: parseQueue(env.result), keepalive: (env.result as { keepalive?: unknown } | null | undefined)?.keepalive ?? null };
}
/** A late joiner's queue; null when the answer is malformed. A slot without one rejects (isNoQueue). */
export async function getCastQueue(slotId: number): Promise<CastQueue | null> {
  return (await getCastQueueState(slotId)).queue;
}

/** Spec §3.3: the street-chat owner stops someone's cast (audit reason HOST). */
export const stopCastAsHost = async (slotId: number) =>
  (await call<{ result?: boolean }>(client.post(`${base(slotId)}/stop`, { asHost: true }))).result === true;
/** Spec §3.3: the host stops a live VIDEO/AUDIO slot through room-stc (main-api's own stop allows only the streamer). */
export const stopLiveAsHost = async (slotId: number) =>
  (await call<{ result?: boolean }>(client.post(`/api/content/live/slots/${slotId}/stop`, {}))).result === true;

/** room-stc sends content codes as the envelope message; the spec also names a string errorCode. Either counts. */
export const hasCastCode = (e: unknown, code: string): boolean =>
  e instanceof CastError && (e.code === code || e.serverMessage === code);
export const isStaleIndex = (e: unknown): e is CastError => e instanceof CastError && e.status === 409 && hasCastCode(e, "STALE_INDEX");
export const staleQueueOf = (e: CastError): CastQueue | null =>
  parseQueue((e.body as { queue?: unknown } | null | undefined)?.queue);
/** 404 NO_QUEUE, or an old room-stc without the route (also 404). */
export const isNoQueue = (e: unknown): boolean => e instanceof CastError && e.status === 404;
